/**
 * Unsent composer text, one entry per chat tab (an unsaved tab's client key or a conversation id), so
 * switching chats and coming back finds the text where it was left. Kept in sessionStorage rather than
 * localStorage: a reload keeps the drafts, but they die with the browser tab and never leak into
 * another one — like the LLM key, a half-written message can hold sensitive text.
 */
const STORAGE_KEY = "istari.ai.composer-drafts";

let drafts: Map<string, string> | null = null;

export function readComposerDraft(key: string): string {
  return getDrafts().get(key) ?? "";
}

export function writeComposerDraft(key: string, text: string): void {
  const current = getDrafts();
  if ((current.get(key) ?? "") === text) return;
  if (text.length === 0) current.delete(key);
  else current.set(key, text);
  persist(current);
}

export function clearComposerDraft(key: string): void {
  writeComposerDraft(key, "");
}

export function clearAllComposerDrafts(): void {
  drafts = new Map();
  persist(drafts);
}

function getDrafts(): Map<string, string> {
  drafts ??= load();
  return drafts;
}

function load(): Map<string, string> {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return new Map();
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return new Map();
    return new Map(
      Object.entries(parsed).filter(
        (entry): entry is [string, string] => typeof entry[1] === "string" && entry[1].length > 0,
      ),
    );
  } catch {
    // sessionStorage unavailable or corrupt — start empty; drafts accrue again as the user types.
    return new Map();
  }
}

function persist(current: Map<string, string>): void {
  try {
    if (current.size === 0) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.fromEntries(current)));
  } catch {
    // sessionStorage unavailable (private mode / quota) — the in-memory map still serves this page load.
  }
}
